// src/pages/UATPage.tsx
// IT Project Portal — User Acceptance Testing
// Interactive guided test runner for Regular User and Admin tracks

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/useAuth';
import { api } from '../services/api';
import './UATPage.css';

// ─── Types ───────────────────────────────────────────────────

type TrackType = 'user' | 'admin';
type ResultType = 'pass' | 'fail' | 'partial' | 'skipped' | null;

interface ScenarioStep {
  id: string;
  title: string;
  icon: string;
  objective: string;
  steps: string[];
  expectedOutcome: string;
  hints?: string[];
  adminOnly?: boolean;
}

interface ScenarioResponse {
  result: ResultType;
  rating: number;
  comments: string;
  timeSpentSec: number;
}

type ResponseMap = Record<string, ScenarioResponse>;

interface TesterInfo {
  name: string;
  email: string;
  role: string;
  department: string;
}

// ─── Scenario Definitions ────────────────────────────────────

const USER_SCENARIOS: ScenarioStep[] = [
  {
    id: 'login',
    title: 'Signing in for the first time',
    icon: '🔐',
    objective: 'Make sure you can get into the portal using your normal Township login — no new password required.',
    steps: [
      'Open the portal link IT sent you.',
      'If asked to sign in, click the Microsoft button and enter your Township email and password — the same ones you use for Outlook.',
      'Once you\'re in, confirm you can see your name somewhere at the top of the screen.',
      'Does your name and department look correct?',
    ],
    expectedOutcome: 'You\'re logged in using your existing Township account. The portal recognized who you are and shows your correct name and department.',
    hints: ['If it asks for a username and password you don\'t recognize, stop here and mark this Failed — that\'s exactly the kind of issue we need to catch.'],
  },
  {
    id: 'dashboard',
    title: 'Getting oriented on the home screen',
    icon: '🏠',
    objective: 'Get a feel for the main screen and make sure the navigation is easy to understand.',
    steps: [
      'Take a minute to look around the home screen.',
      'Find the navigation menu — it should be on the left side.',
      'Click the bell icon near the top right to see if you have any notifications.',
      'Close the notification panel and return to the home screen.',
      'Does the layout feel clear and easy to navigate?',
    ],
    expectedOutcome: 'The home screen loads without errors. The navigation makes sense at a glance. The notification panel opens and closes cleanly.',
    hints: ['You might not have any notifications yet — that\'s fine. We just want to make sure the bell icon works.'],
  },
  {
    id: 'new_request_start',
    title: 'Starting a new IT project request',
    icon: '📝',
    objective: 'Try filling out a request for a made-up project to see how the process feels.',
    steps: [
      'Find and click "New Request" in the left navigation.',
      'You\'ll be walked through a short form, a few steps at a time.',
      'On the first step, enter a title like "UAT Test Project" and write a brief description of a made-up need.',
      'Work through each step, filling in what you can.',
      'When you reach the last step, look for a "Save as Draft" option and click it — don\'t submit yet.',
      'Was the form straightforward? Were any questions confusing or unclear?',
    ],
    expectedOutcome: 'The form saves your draft without errors. Your test project appears on the home screen labeled as a draft.',
    hints: ['You can\'t break anything by entering made-up information here. If a question confused you, that\'s valuable feedback — write it in the comments.'],
  },
  {
    id: 'draft_resume',
    title: 'Coming back to a saved draft',
    icon: '↩️',
    objective: 'Make sure you can find a draft you started earlier and pick up where you left off.',
    steps: [
      'Go back to the home screen.',
      'Find the draft you just saved.',
      'Click on it to open it.',
      'Make a small change to your description.',
      'Save it as a draft again.',
      'Was it easy to find your draft and get back into it?',
    ],
    expectedOutcome: 'Your draft was easy to find, opened correctly, accepted your changes, and saved again without any errors.',
  },
  {
    id: 'submit_request',
    title: 'Submitting your request for approval',
    icon: '🚀',
    objective: 'Submit your test request and see what happens next — who it goes to and what the status looks like.',
    steps: [
      'Open your draft project again.',
      'Work through the form to the final review step.',
      'Before submitting, look at the section that shows who will approve your request.',
      'Does it show the right people for your department?',
      'Click the submit button.',
      'What does the status change to after you submit?',
    ],
    expectedOutcome: 'Your request is submitted and the status changes to something like "Pending Approval." The approval steps are visible and show the right people.',
    hints: ['The approvers shown should match your department director and appropriate manager. If they look wrong, note that in your comments.'],
  },
  {
    id: 'track_status',
    title: 'Checking where your request stands',
    icon: '🔍',
    objective: 'Find out how to see the current status of a request you\'ve already submitted.',
    steps: [
      'Find your submitted project — try clicking "Projects" in the left navigation.',
      'Open your test project.',
      'Is all the information you entered showing correctly?',
      'Find the section that shows the approval steps. Who still needs to approve it? Who has already acted?',
      'Does the status information feel clear and easy to understand?',
    ],
    expectedOutcome: 'You can easily find your project, see its current status, and understand where it is in the approval process without any confusion.',
  },
  {
    id: 'attach_file',
    title: 'Attaching a supporting document',
    icon: '📎',
    objective: 'Try uploading a file to your project — like a document that helps explain your request.',
    steps: [
      'Open your project.',
      'Find the "Attachments" section.',
      'Click the upload button and choose any small file from your computer — a Word doc, PDF, or image works fine.',
      'Wait for it to finish uploading.',
      'Confirm the file appears with its name listed.',
      'Click the download button next to the file and confirm it opens or saves to your computer.',
    ],
    expectedOutcome: 'The file uploaded without errors and appears in the list. Downloading it again works correctly.',
    hints: ['If you don\'t have a file handy, take a screenshot of your desktop and upload that.'],
  },
  {
    id: 'comment',
    title: 'Leaving a note on your project',
    icon: '💬',
    objective: 'Try adding a comment to your project — like a message to whoever will be reviewing it.',
    steps: [
      'Open your project.',
      'Find the comments section.',
      'Type a short message — for example, "This project is needed to support our team\'s goals this quarter."',
      'Post the comment.',
      'Confirm your message appears with your name and the time you posted it.',
    ],
    expectedOutcome: 'Your comment posted successfully and is visible with your name and a timestamp.',
  },
  {
    id: 'revision_response',
    title: 'What it looks like when your request is sent back',
    icon: '🔄',
    objective: 'Understand what happens when an approver asks you to make changes before they can approve.',
    steps: [
      'IT will send your test project back for revision before you start this scenario — check your project status.',
      'If your project shows a status indicating revision is needed, open it.',
      'Read the comment the approver left explaining what needs to change.',
      'Make a small update to your project.',
      'Resubmit the request.',
      'Was it clear what you needed to do? Did you feel like you had to start over from scratch?',
    ],
    expectedOutcome: 'You could see the revision request and the approver\'s comment, make a change, and resubmit without losing your original information.',
    hints: ['If your project hasn\'t been sent back for revision yet, skip this one and let IT know — they can trigger it for you.'],
  },
  {
    id: 'all_projects',
    title: 'Browsing all IT projects',
    icon: '📋',
    objective: 'See the full list of IT projects across the organization and try narrowing it down.',
    steps: [
      'Click "Projects" in the left navigation.',
      'Browse the list — you should see projects from across the organization.',
      'Try typing a word in the search box and see if the list narrows down.',
      'Try filtering to show only projects with a certain status.',
      'Click any project to open it.',
      'Is it useful to have visibility into all projects, not just your own?',
    ],
    expectedOutcome: 'The projects list loads, search works, filtering by status works, and clicking a project shows its details.',
  },
  {
    id: 'requirements_tab',
    title: 'Viewing project requirements and scope',
    icon: '📌',
    objective: 'Find the requirements section of a project and see how IT tracks what needs to happen.',
    steps: [
      'Open any project that has been submitted.',
      'Look for a "Requirements" section.',
      'Browse any items listed — these are questions or tasks IT is tracking for the project.',
      'Does it make sense what this section is for?',
      'Would it be helpful to see this information as a project owner?',
    ],
    expectedOutcome: 'The Requirements section is visible and the purpose is understandable — it shows what\'s being tracked to move the project forward.',
    hints: ['This section is managed by IT. You\'re just reviewing it here to tell us whether it\'s clear and useful from your perspective.'],
  },
  {
    id: 'notifications_full',
    title: 'Getting notified when something changes',
    icon: '🔔',
    objective: 'Check that the notification system keeps you informed without you having to check back manually.',
    steps: [
      'Click the bell icon at the top of the screen.',
      'Do you see any notifications? If yes, click one and confirm it takes you to the right project.',
      'Go back and mark any notifications as read.',
      'Confirm the unread indicator on the bell changes or disappears.',
      'Overall — would notifications like these help you stay on top of your requests?',
    ],
    expectedOutcome: 'Notifications appear for project activity. Clicking one goes to the right place. Marking them read works.',
    hints: ['If you have no notifications, ask IT to approve or comment on your test project and come back to this scenario.'],
  },
];

const ADMIN_SCENARIOS: ScenarioStep[] = [
  {
    id: 'admin_panel_nav',
    title: 'Getting around the Admin panel',
    icon: '⚙️',
    objective: 'Make sure all parts of the Admin panel load and feel logically organized.',
    steps: [
      'Click "Admin" in the left navigation.',
      'Click through the main tabs: Departments, Users & Roles, Projects, and Settings.',
      'Inside Settings, click each sub-section: Form Settings, Portal Settings, Workflow, and System.',
      'Does everything load without errors?',
      'Does the organization of the admin panel feel logical and easy to navigate?',
    ],
    expectedOutcome: 'Every section loads without errors. The layout feels intuitive and information is where you\'d expect it.',
    adminOnly: true,
  },
  {
    id: 'admin_add_user',
    title: 'Adding a new user manually',
    icon: '➕',
    objective: 'Try adding a user to the system directly, without waiting for them to log in on their own.',
    steps: [
      'Go to Admin → Users & Roles.',
      'Find and click the "Add User" button.',
      'Fill in a name, email, and role for a test user.',
      'Save the new user.',
      'Confirm the user appears in the list.',
      'Was the process quick and clear?',
    ],
    expectedOutcome: 'The new user is created and immediately appears in the list with the correct information.',
    adminOnly: true,
  },
  {
    id: 'admin_user_roles',
    title: 'Changing a user\'s role',
    icon: '👥',
    objective: 'Make sure updating someone\'s access level is easy and takes effect right away.',
    steps: [
      'In Admin → Users & Roles, find any user in the list.',
      'Change their role using the role options.',
      'Save the change.',
      'Confirm the updated role shows correctly in the list.',
      'Try selecting several users at once and updating their roles in bulk, if that option is available.',
    ],
    expectedOutcome: 'Individual role changes save correctly. Bulk updates work when multiple users are selected.',
    adminOnly: true,
  },
  {
    id: 'admin_departments',
    title: 'Keeping department contacts up to date',
    icon: '🏢',
    objective: 'Make sure you can easily update who the Director and manager are for any department.',
    steps: [
      'Go to Admin → Departments.',
      'Confirm all Township departments are listed.',
      'Open any department and verify the Director and EMT contact information looks correct.',
      'Make a small test edit to a contact name.',
      'Save the change and confirm it updated.',
      'Revert the change afterward.',
    ],
    expectedOutcome: 'All departments are listed. Editing a contact saves correctly and the list reflects the change immediately.',
    adminOnly: true,
  },
  {
    id: 'admin_projects',
    title: 'Admin control over project details',
    icon: '🛠️',
    objective: 'Confirm IT can update project details — like adding notes or adjusting priority — without going through the approval process.',
    steps: [
      'Go to Admin → Projects.',
      'Find your test project.',
      'Open the edit view for it.',
      'Add something in the IT Notes field — for example, "Under review by IT."',
      'Change the priority level.',
      'Save and confirm the changes show up on the project.',
    ],
    expectedOutcome: 'IT Notes and priority are saved and visible when viewing the project.',
    adminOnly: true,
  },
  {
    id: 'admin_approval_action',
    title: 'Acting on a pending request',
    icon: '✅',
    objective: 'Walk through the full experience of reviewing and approving a request as an approver would.',
    steps: [
      'Click "Approvals" in the left navigation.',
      'Find your test project in the pending list.',
      'Open it and read through what was submitted.',
      'Click "Approve" and add a brief comment.',
      'Confirm the project status updates and moves to the next approval step.',
      'Did you have everything you needed to make a decision?',
    ],
    expectedOutcome: 'The approval is recorded, the project moves forward, and the change is visible when you view the project.',
    adminOnly: true,
    hints: ['You can also try "Request Revision" to send it back for changes, or "Deny" to close it. Use your test project so no real requests are affected.'],
  },
  {
    id: 'admin_revision_deny',
    title: 'Sending a request back or closing it',
    icon: '↩️',
    objective: 'Test what it looks like to send a request back for changes or deny it entirely.',
    steps: [
      'Find a pending test project in your approvals queue.',
      'Choose "Request Revision" and add a comment explaining what needs to change.',
      'Confirm the project status shows it\'s been sent back.',
      'Open the project — would the submitter be able to clearly understand your comment?',
      'Optionally, try "Deny" on a second test project and confirm it closes the request.',
    ],
    expectedOutcome: 'Revision request sends the project back with your comment clearly visible to the submitter. Denial closes the request with a reason.',
    adminOnly: true,
  },
  {
    id: 'admin_workflow_config',
    title: 'Adjusting the approval chain',
    icon: '🔀',
    objective: 'Make sure IT can change which approval steps are required without any technical help.',
    steps: [
      'Go to Admin → Settings → Workflow.',
      'You\'ll see a list of approval steps — Director, EMT, IT, and possibly others.',
      'Toggle one step off and save.',
      'Submit a test request (or ask someone to) and confirm that step is now skipped in the approval chain.',
      'Turn the step back on.',
      'Does this feel like something IT could manage on their own day-to-day?',
    ],
    expectedOutcome: 'Turning a step off and back on works and persists. New submissions reflect the change in their approval chain.',
    adminOnly: true,
  },
  {
    id: 'admin_form_settings',
    title: 'Controlling what appears on the request form',
    icon: '📋',
    objective: 'Confirm IT can show or hide fields on the intake form without any coding.',
    steps: [
      'Go to Admin → Settings → Form Settings.',
      'Look at the list of fields that can appear on the new request form.',
      'Turn one optional field off and save.',
      'Go to New Request and confirm that field no longer shows.',
      'Return to Form Settings and turn it back on.',
    ],
    expectedOutcome: 'Hiding a field removes it from the form right away. Re-enabling it brings it back.',
    adminOnly: true,
  },
  {
    id: 'admin_portal_settings',
    title: 'Portal-wide settings',
    icon: '🔧',
    objective: 'Review the broader portal settings and make sure the key controls work as expected.',
    steps: [
      'Go to Admin → Settings → Portal Settings.',
      'Review what settings are available.',
      'Try changing one setting and saving.',
      'Confirm the change takes effect.',
      'Do the available settings give IT the right level of control?',
    ],
    expectedOutcome: 'Settings load correctly. Changes save and take effect as expected.',
    adminOnly: true,
  },
  {
    id: 'admin_email_system',
    title: 'Managing email notifications safely',
    icon: '📧',
    objective: 'Confirm emails can be managed without accidentally notifying real staff during testing.',
    steps: [
      'Go to Admin → Settings → System.',
      'Find the Test Mode toggle and check its current state.',
      'When Test Mode is on, a banner should appear at the top of the screen — confirm that.',
      'Review the list of who receives emails when Test Mode is active.',
      'If it\'s safe to do so, trigger an action on a test project and confirm the email went to the test address, not the real approver.',
      'Make sure Test Mode is back to its original state before you finish.',
    ],
    expectedOutcome: 'Test Mode works as a safety net — emails go to IT only when it\'s active. The banner makes it clear when Test Mode is on.',
    adminOnly: true,
    hints: ['If you\'re unsure whether it\'s safe to send a test email right now, just review the settings and mark this scenario based on what you can observe.'],
  },
  {
    id: 'admin_masquerade',
    title: 'Viewing the portal as another role',
    icon: '🎭',
    objective: 'Confirm IT admins can see what other users — like Directors or regular staff — experience in the portal.',
    steps: [
      'Click your name or avatar in the top right corner.',
      'Look for a "View As" option.',
      'Switch to the "Director" role.',
      'A banner should appear confirming you\'re in a simulated view.',
      'Go to the Approvals section and see what a Director would see there.',
      'Switch back to your normal Admin view.',
      'Was it clear the whole time that you were in a simulated view, not your real account?',
    ],
    expectedOutcome: 'Switching roles changes what\'s visible in the app. A clear banner shows you\'re simulating another role. Switching back works cleanly.',
    adminOnly: true,
    hints: ['No real changes are made when using View As — it\'s purely a way to preview what another role sees.'],
  },
  {
    id: 'admin_audit_log',
    title: 'Reviewing what\'s happened in the portal',
    icon: '📜',
    objective: 'Confirm the audit log shows a full, accurate record of activity that can be exported for review.',
    steps: [
      'Find the Audit Log in the Admin navigation.',
      'Confirm it shows activity from this testing session.',
      'Try searching by your name or your test project.',
      'Try filtering by a date range.',
      'Click Export and confirm a file downloads that you could open in Excel.',
      'Is there enough detail to understand who did what and when?',
    ],
    expectedOutcome: 'Audit log shows accurate activity. Search and date filter work. Export downloads a file that opens correctly.',
    adminOnly: true,
  },
  {
    id: 'admin_login_history',
    title: 'Reviewing who has logged in',
    icon: '🕐',
    objective: 'Confirm the portal is tracking login and logout activity in a way that\'s useful for access reviews.',
    steps: [
      'Find Login History in the Admin navigation.',
      'Confirm it shows login and logout times for portal users.',
      'Check the summary numbers at the top — do they look accurate?',
      'Search for your own name.',
      'Export the login history and confirm the file downloads.',
      'Would this be useful for a yearly access review or compliance audit?',
    ],
    expectedOutcome: 'Login history loads with accurate data. Summary stats are correct. Search and export both work.',
    adminOnly: true,
  },
  {
    id: 'admin_clear_audit',
    title: 'Cleaning up test data after UAT',
    icon: '🧹',
    objective: 'Confirm IT can remove test entries from the audit log once UAT is complete.',
    steps: [
      'In the Audit Log, look for a way to clear or remove entries.',
      'Select just your own test entries to remove.',
      'Confirm the entries are removed.',
      'Did the process ask you to confirm before deleting — or did it just delete immediately?',
    ],
    expectedOutcome: 'Selected audit entries are removed after a confirmation step. The log updates to reflect the deletion.',
    adminOnly: true,
  },
];

// ─── Sub-Components ───────────────────────────────────────────

function StarRating({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="uat-stars" role="group" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          className={`uat-star ${(hovered || value) >= star ? 'uat-star--filled' : ''}`}
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          aria-label={`${star} star${star !== 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }) {
  const pct = total > 0 ? Math.round((current / total) * 100) : 0;
  return (
    <div className="uat-progress-wrap">
      <div className="uat-progress-bar">
        <div className="uat-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="uat-progress-label">{current} of {total} complete</span>
    </div>
  );
}

function ResultBadge({ result }: { result: ResultType }) {
  if (!result) return null;
  const map = {
    pass: { label: 'Pass ✓', cls: 'uat-badge--pass' },
    fail: { label: 'Fail ✗', cls: 'uat-badge--fail' },
    partial: { label: 'Partial ~', cls: 'uat-badge--partial' },
    skipped: { label: 'Skipped', cls: 'uat-badge--skipped' },
  };
  const { label, cls } = map[result];
  return <span className={`uat-badge ${cls}`}>{label}</span>;
}

// ─── Main Component ───────────────────────────────────────────

export default function UATPage() {
  const { user, dbUserID, isAdmin } = useAuth();

  // Phase: 'intro' | 'register' | 'testing' | 'wrap-up' | 'done'
  const [phase, setPhase] = useState<'intro' | 'register' | 'testing' | 'wrap-up' | 'done'>('intro');
  const [track, setTrack] = useState<TrackType>('user');
  const [testerInfo, setTesterInfo] = useState<TesterInfo>({
    name: user?.name || '',
    email: user?.email || '',
    role: isAdmin ? 'IT.Admin' : 'Staff',
    department: '',
  });
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [responses, setResponses] = useState<ResponseMap>({});
  const [overallRating, setOverallRating] = useState(0);
  const [overallComments, setOverallComments] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Timer for current scenario
  const scenarioStartRef = useRef<number>(Date.now());
  const [elapsedSec, setElapsedSec] = useState(0);

  const scenarios = track === 'admin' ? [...USER_SCENARIOS, ...ADMIN_SCENARIOS] : USER_SCENARIOS;
  const currentScenario = scenarios[currentIdx];
  const completedCount = Object.keys(responses).filter(k => responses[k]?.result).length;

  // Reset timer when scenario changes
  useEffect(() => {
    scenarioStartRef.current = Date.now();
    setElapsedSec(0);
    setShowHints(false);
  }, [currentIdx]);

  // Live timer
  useEffect(() => {
    if (phase !== 'testing') return;
    const t = setInterval(() => {
      setElapsedSec(Math.floor((Date.now() - scenarioStartRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, [phase, currentIdx]);

  // Pre-fill from auth
  useEffect(() => {
    if (user) {
      setTesterInfo(prev => ({
        ...prev,
        name: prev.name || user.name || '',
        email: prev.email || user.email || '',
        role: prev.role || (isAdmin ? 'IT.Admin' : 'Staff'),
      }));
    }
  }, [user, isAdmin]);

  const handleRegister = async () => {
    if (!testerInfo.name || !testerInfo.email) {
      setError('Please fill in your name and email.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      const result = await api.uat.createSession({
        testerName: testerInfo.name,
        testerEmail: testerInfo.email,
        testerRole: testerInfo.role,
        track,
        userId: dbUserID || undefined,
      });
      setSessionId(result.sessionId);
      setPhase('testing');
    } catch {
      setError('Could not start session. Please try again or contact IT.');
    } finally {
      setSaving(false);
    }
  };

  const saveCurrentResponse = useCallback(async (resp: ScenarioResponse) => {
    if (!sessionId || !currentScenario) return;
    try {
      await api.uat.saveResponse(sessionId, {
        scenarioId: currentScenario.id,
        scenarioTitle: currentScenario.title,
        result: resp.result!,
        rating: resp.rating || undefined,
        comments: resp.comments || undefined,
        timeSpentSec: resp.timeSpentSec,
      });
    } catch {
      // Non-fatal — don't block navigation
      console.warn('Failed to save UAT response, continuing anyway.');
    }
  }, [sessionId, currentScenario]);

  const handleResultSelect = (result: ResultType) => {
    const updated: ScenarioResponse = {
      ...(responses[currentScenario.id] || { rating: 0, comments: '', timeSpentSec: 0 }),
      result,
      timeSpentSec: Math.floor((Date.now() - scenarioStartRef.current) / 1000),
    };
    setResponses(prev => ({ ...prev, [currentScenario.id]: updated }));
    saveCurrentResponse(updated);
  };

  const handleRatingChange = (rating: number) => {
    const updated: ScenarioResponse = {
      ...(responses[currentScenario.id] || { result: null, comments: '', timeSpentSec: 0 }),
      rating,
      timeSpentSec: Math.floor((Date.now() - scenarioStartRef.current) / 1000),
    };
    setResponses(prev => ({ ...prev, [currentScenario.id]: updated }));
  };

  const handleCommentChange = (comments: string) => {
    setResponses(prev => ({
      ...prev,
      [currentScenario.id]: {
        ...(prev[currentScenario.id] || { result: null, rating: 0, timeSpentSec: 0 }),
        comments,
      },
    }));
  };

  const handleNext = async () => {
    // Auto-save current before moving
    const resp = responses[currentScenario.id];
    if (resp) await saveCurrentResponse({ ...resp, timeSpentSec: Math.floor((Date.now() - scenarioStartRef.current) / 1000) });

    if (currentIdx < scenarios.length - 1) {
      setCurrentIdx(i => i + 1);
    } else {
      setPhase('wrap-up');
    }
  };

  const handlePrev = () => {
    if (currentIdx > 0) setCurrentIdx(i => i - 1);
  };

  const handleSkip = async () => {
    const skippedResp: ScenarioResponse = {
      result: 'skipped',
      rating: 0,
      comments: '',
      timeSpentSec: Math.floor((Date.now() - scenarioStartRef.current) / 1000),
    };
    setResponses(prev => ({ ...prev, [currentScenario.id]: skippedResp }));
    await saveCurrentResponse(skippedResp);
    if (currentIdx < scenarios.length - 1) {
      setCurrentIdx(i => i + 1);
    } else {
      setPhase('wrap-up');
    }
  };

  const handleSubmitFinal = async () => {
    if (!sessionId) return;
    setSaving(true);
    setError(null);
    try {
      await api.uat.submitSession(sessionId, {
        overallRating: overallRating || undefined,
        overallComments: overallComments || undefined,
      });
      setSubmitted(true);
      setPhase('done');
    } catch {
      setError('Submission failed. Please try again or contact IT.');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (sec: number) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  // ── Render: Intro ─────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="uat-page">
        <div className="uat-intro-card">
          <div className="uat-intro-badge">User Acceptance Testing</div>
          <h1 className="uat-intro-title">IT Project Portal UAT</h1>
          <p className="uat-intro-sub">
            Welcome! This guided test will walk you through each feature of the portal step-by-step.
            Your responses are saved automatically and help IT validate that everything works before full rollout.
          </p>

          <div className="uat-track-grid">
            <button
              className={`uat-track-card ${track === 'user' ? 'uat-track-card--active' : ''}`}
              onClick={() => setTrack('user')}
            >
              <span className="uat-track-icon">👤</span>
              <strong>Regular User Track</strong>
              <p>For all Township staff. Covers submitting requests, tracking projects, attachments, and comments.</p>
              <span className="uat-track-count">{USER_SCENARIOS.length} scenarios · ~45 min</span>
            </button>
            <button
              className={`uat-track-card ${track === 'admin' ? 'uat-track-card--active' : ''}`}
              onClick={() => { if (isAdmin) setTrack('admin'); }}
              disabled={!isAdmin}
              title={!isAdmin ? 'Admin track requires IT.Admin role' : undefined}
            >
              <span className="uat-track-icon">⚙️</span>
              <strong>Admin Track</strong>
              <p>For IT Admins. Includes all user scenarios plus admin panel, workflow config, audit log, and masquerade.</p>
              <span className="uat-track-count">{USER_SCENARIOS.length + ADMIN_SCENARIOS.length} scenarios · ~90 min</span>
              {!isAdmin && <span className="uat-track-locked">🔒 IT Admin only</span>}
            </button>
          </div>

          <div className="uat-intro-info">
            <h3>What to expect</h3>
            <ul>
              <li>Each scenario gives you step-by-step instructions — just follow along in the portal.</li>
              <li>Mark each scenario Pass, Partial, or Fail based on what you observe.</li>
              <li>Leave comments on anything confusing, broken, or that could be improved.</li>
              <li>All responses are saved automatically — you can close the browser and resume if needed.</li>
            </ul>
          </div>

          <button className="uat-btn uat-btn--primary uat-btn--lg" onClick={() => setPhase('register')}>
            Start Testing →
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Register ──────────────────────────────────────
  if (phase === 'register') {
    return (
      <div className="uat-page">
        <div className="uat-register-card">
          <button className="uat-back-btn" onClick={() => setPhase('intro')}>← Back</button>
          <h2>Before we begin</h2>
          <p className="uat-register-sub">Confirm your information so we can attribute your test results correctly.</p>

          <div className="uat-form-grid">
            <label className="uat-label">
              Your Name *
              <input
                className="uat-input"
                value={testerInfo.name}
                onChange={e => setTesterInfo(p => ({ ...p, name: e.target.value }))}
                placeholder="First Last"
              />
            </label>
            <label className="uat-label">
              Your Email *
              <input
                className="uat-input"
                type="email"
                value={testerInfo.email}
                onChange={e => setTesterInfo(p => ({ ...p, email: e.target.value }))}
                placeholder="you@thewoodlands-tx.gov"
              />
            </label>
            <label className="uat-label">
              Your Role
              <select
                className="uat-input"
                value={testerInfo.role}
                onChange={e => setTesterInfo(p => ({ ...p, role: e.target.value }))}
              >
                <option value="Staff">Staff</option>
                <option value="Director">Director</option>
                <option value="EMT">EMT</option>
                <option value="IT.Approver">IT Approver</option>
                <option value="IT.Admin">IT Admin</option>
              </select>
            </label>
            <label className="uat-label">
              Department
              <input
                className="uat-input"
                value={testerInfo.department}
                onChange={e => setTesterInfo(p => ({ ...p, department: e.target.value }))}
                placeholder="e.g. Finance, Public Works"
              />
            </label>
          </div>

          <div className="uat-track-summary">
            Track selected: <strong>{track === 'admin' ? 'Admin' : 'Regular User'}</strong> ({scenarios.length} scenarios)
          </div>

          {error && <div className="uat-error">{error}</div>}

          <button
            className="uat-btn uat-btn--primary uat-btn--lg"
            onClick={handleRegister}
            disabled={saving}
          >
            {saving ? 'Starting…' : 'Begin UAT →'}
          </button>
        </div>
      </div>
    );
  }

  // ── Render: Testing ───────────────────────────────────────
  if (phase === 'testing') {
    const resp = responses[currentScenario.id] || null;
    const hasResult = !!resp?.result;

    return (
      <div className="uat-page uat-page--testing">
        {/* Sidebar */}
        <aside className="uat-sidebar">
          <div className="uat-sidebar-header">
            <span className="uat-sidebar-logo">🏛️</span>
            <div>
              <div className="uat-sidebar-title">UAT Session</div>
              <div className="uat-sidebar-sub">{track === 'admin' ? 'Admin Track' : 'User Track'}</div>
            </div>
          </div>

          <ProgressBar current={completedCount} total={scenarios.length} />

          <nav className="uat-nav">
            {scenarios.map((s, i) => {
              const r = responses[s.id];
              return (
                <button
                  key={s.id}
                  className={`uat-nav-item ${i === currentIdx ? 'uat-nav-item--active' : ''} ${r?.result ? `uat-nav-item--${r.result}` : ''}`}
                  onClick={() => setCurrentIdx(i)}
                >
                  <span className="uat-nav-icon">{s.icon}</span>
                  <span className="uat-nav-label">{s.title}</span>
                  {r?.result && <ResultBadge result={r.result} />}
                </button>
              );
            })}
          </nav>

          <div className="uat-sidebar-footer">
            <div className="uat-tester-info">
              <strong>{testerInfo.name}</strong>
              <span>{testerInfo.role}</span>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="uat-main">
          {/* Header */}
          <div className="uat-scenario-header">
            <div className="uat-scenario-meta">
              <span className="uat-scenario-num">Scenario {currentIdx + 1} of {scenarios.length}</span>
              {currentScenario.adminOnly && <span className="uat-badge uat-badge--admin">Admin Only</span>}
              <span className="uat-timer">⏱ {formatTime(elapsedSec)}</span>
            </div>
            <h2 className="uat-scenario-title">
              <span className="uat-scenario-icon">{currentScenario.icon}</span>
              {currentScenario.title}
            </h2>
          </div>

          {/* Objective */}
          <div className="uat-objective">
            <strong>Objective:</strong> {currentScenario.objective}
          </div>

          {/* Steps */}
          <div className="uat-steps-card">
            <h3 className="uat-steps-title">Steps to follow</h3>
            <p className="uat-steps-note">Open the portal in a separate browser tab and follow these steps:</p>
            <ol className="uat-steps-list">
              {currentScenario.steps.map((step, i) => (
                <li key={i} className="uat-step-item">
                  <span className="uat-step-num">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>

            <div className="uat-expected">
              <span className="uat-expected-label">✓ Expected outcome:</span>
              <span>{currentScenario.expectedOutcome}</span>
            </div>

            {currentScenario.hints && currentScenario.hints.length > 0 && (
              <div className="uat-hints-wrap">
                <button className="uat-hints-toggle" onClick={() => setShowHints(h => !h)}>
                  {showHints ? '▼' : '▶'} Hints ({currentScenario.hints.length})
                </button>
                {showHints && (
                  <ul className="uat-hints-list">
                    {currentScenario.hints.map((h, i) => <li key={i}>{h}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Result capture */}
          <div className="uat-capture-card">
            <h3>How did it go?</h3>

            <div className="uat-result-btns">
              {(['pass', 'partial', 'fail'] as const).map(r => (
                <button
                  key={r}
                  className={`uat-result-btn uat-result-btn--${r} ${resp?.result === r ? 'uat-result-btn--selected' : ''}`}
                  onClick={() => handleResultSelect(r)}
                >
                  {r === 'pass' ? '✅ Passed' : r === 'partial' ? '⚠️ Partially Worked' : '❌ Failed'}
                </button>
              ))}
            </div>

            <label className="uat-label" style={{ marginTop: '1rem' }}>
              Rate this experience
              <StarRating value={resp?.rating || 0} onChange={handleRatingChange} />
            </label>

            <label className="uat-label">
              Comments / issues found
              <textarea
                className="uat-textarea"
                placeholder="Describe what happened, any error messages, or suggestions…"
                value={resp?.comments || ''}
                onChange={e => handleCommentChange(e.target.value)}
                rows={3}
              />
            </label>
          </div>

          {/* Navigation */}
          <div className="uat-nav-btns">
            <button className="uat-btn uat-btn--ghost" onClick={handlePrev} disabled={currentIdx === 0}>
              ← Previous
            </button>
            <button className="uat-btn uat-btn--ghost uat-btn--skip" onClick={handleSkip}>
              Skip
            </button>
            <button
              className="uat-btn uat-btn--primary"
              onClick={handleNext}
              disabled={!hasResult}
            >
              {currentIdx < scenarios.length - 1 ? 'Next Scenario →' : 'Finish Testing →'}
            </button>
          </div>
          {!hasResult && (
            <p className="uat-nav-hint">Mark a result above to continue, or click Skip to come back later.</p>
          )}
        </main>
      </div>
    );
  }

  // ── Render: Wrap-Up ───────────────────────────────────────
  if (phase === 'wrap-up') {
    const passCount = Object.values(responses).filter(r => r.result === 'pass').length;
    const failCount = Object.values(responses).filter(r => r.result === 'fail').length;
    const partialCount = Object.values(responses).filter(r => r.result === 'partial').length;
    const skippedCount = Object.values(responses).filter(r => r.result === 'skipped').length;

    return (
      <div className="uat-page">
        <div className="uat-wrapup-card">
          <div className="uat-wrapup-icon">🎉</div>
          <h2>Almost done!</h2>
          <p>You've completed all {scenarios.length} scenarios. Share your overall impressions before submitting.</p>

          <div className="uat-results-summary">
            <div className="uat-result-stat uat-result-stat--pass"><span>{passCount}</span>Passed</div>
            <div className="uat-result-stat uat-result-stat--partial"><span>{partialCount}</span>Partial</div>
            <div className="uat-result-stat uat-result-stat--fail"><span>{failCount}</span>Failed</div>
            <div className="uat-result-stat uat-result-stat--skip"><span>{skippedCount}</span>Skipped</div>
          </div>

          <div className="uat-capture-card">
            <label className="uat-label">
              Overall portal rating
              <StarRating value={overallRating} onChange={setOverallRating} />
            </label>
            <label className="uat-label">
              Overall comments & suggestions
              <textarea
                className="uat-textarea"
                placeholder="What worked well? What needs improvement? Any features you'd like to see added?"
                value={overallComments}
                onChange={e => setOverallComments(e.target.value)}
                rows={5}
              />
            </label>
          </div>

          <div className="uat-wrapup-nav">
            <button className="uat-btn uat-btn--ghost" onClick={() => { setPhase('testing'); }}>
              ← Back to Scenarios
            </button>
            {error && <div className="uat-error">{error}</div>}
            <button
              className="uat-btn uat-btn--primary uat-btn--lg"
              onClick={handleSubmitFinal}
              disabled={saving || submitted}
            >
              {saving ? 'Submitting…' : 'Submit UAT Results →'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Done ──────────────────────────────────────────
  return (
    <div className="uat-page">
      <div className="uat-done-card">
        <div className="uat-done-icon">✅</div>
        <h2>Thank you, {testerInfo.name}!</h2>
        <p>Your UAT results have been saved. IT will review all submissions and follow up on any issues found.</p>
        <div className="uat-done-id">Session ID: <strong>#{sessionId}</strong></div>
        <p className="uat-done-note">
          Reference this ID if you need to discuss specific results with the IT team.
          Your responses are included in the year-end audit report.
        </p>
        <a href="/" className="uat-btn uat-btn--primary">Return to Portal →</a>
      </div>
    </div>
  );
}
